import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Plus, Minus, ChevronDown, ChevronUp, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Medical Questionnaire Component
const MedicalQuestionnaire = ({ formData, setFormData, attendeeIndex = null }) => {
  const prefix = attendeeIndex !== null ? `attendee_${attendeeIndex}_` : '';
  const [expandedBoxes, setExpandedBoxes] = useState({});

  const getValue = (field) => {
    if (attendeeIndex !== null) {
      return formData.additionalAttendees?.[attendeeIndex]?.medical?.[field] ?? null;
    }
    return formData.medical?.[field] ?? null;
  };

  const setValue = (field, value) => {
    if (attendeeIndex !== null) {
      setFormData(prev => {
        const updated = [...(prev.additionalAttendees || [])];
        if (!updated[attendeeIndex]) updated[attendeeIndex] = {};
        if (!updated[attendeeIndex].medical) updated[attendeeIndex].medical = {};
        updated[attendeeIndex].medical[field] = value;
        return { ...prev, additionalAttendees: updated };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        medical: { ...prev.medical, [field]: value }
      }));
    }
  };

  const toggleBox = (box) => {
    setExpandedBoxes(prev => ({ ...prev, [box]: !prev[box] }));
  };

  const YesNoQuestion = ({ id, question, goToBox = null, hasAsterisk = false }) => {
    const value = getValue(id);
    const showBox = goToBox && value === true;
    
    return (
      <div className="py-3 border-b border-gray-100 last:border-0">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-700">{question}</p>
            {hasAsterisk && <span className="text-xs text-orange-600">* Requires physician evaluation if Yes</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setValue(id, true)}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                value === true 
                  ? 'bg-red-100 border-red-300 text-red-700' 
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setValue(id, false)}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                value === false 
                  ? 'bg-green-100 border-green-300 text-green-700' 
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              No
            </button>
          </div>
        </div>
        {showBox && goToBox && (
          <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Please answer the questions in Box {goToBox} below
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          <strong>Note to women:</strong> If you are pregnant, or attempting to become pregnant, do not dive.
        </p>
      </div>

      {/* Main Questions */}
      <div className="bg-white border rounded-lg p-4">
        <h5 className="font-semibold text-[#0A2342] mb-3">Health Screening Questions</h5>
        
        <YesNoQuestion 
          id="q1_lungs_heart" 
          question="I have had problems with my lungs, breathing, heart and/or blood affecting my normal physical or mental performance."
          goToBox="A"
        />
        
        <YesNoQuestion 
          id="q2_over_45" 
          question="I am over 45 years of age."
          goToBox="B"
        />
        
        <YesNoQuestion 
          id="q3_exercise" 
          question="I struggle to perform moderate exercise (walk 1.6km in 14 minutes or swim 200m without resting), OR I have been unable to participate in normal physical activity due to fitness or health reasons within the past 12 months."
          hasAsterisk
        />
        
        <YesNoQuestion 
          id="q4_eyes_ears" 
          question="I have had problems with my eyes, ears, or nasal passages/sinuses."
          goToBox="C"
        />
        
        <YesNoQuestion 
          id="q5_surgery" 
          question="I have had surgery within the last 12 months, OR I have ongoing problems related to past surgery."
          hasAsterisk
        />
        
        <YesNoQuestion 
          id="q6_neurological" 
          question="I have lost consciousness, had migraine headaches, seizures, stroke, significant head injury, or suffer from persistent neurologic injury or disease."
          goToBox="D"
        />
        
        <YesNoQuestion 
          id="q7_psychological" 
          question="I am currently undergoing treatment (or have required treatment within the last five years) for psychological problems, personality disorder, panic attacks, or an addiction to drugs or alcohol; or, I have been diagnosed with a learning or developmental disability."
          goToBox="E"
        />
        
        <YesNoQuestion 
          id="q8_back_diabetes" 
          question="I have had back problems, hernia, ulcers, or diabetes."
          goToBox="F"
        />
        
        <YesNoQuestion 
          id="q9_stomach" 
          question="I have had stomach or intestine problems, including recent diarrhea."
          goToBox="G"
        />
        
        <YesNoQuestion 
          id="q10_medications" 
          question="I am taking prescription medications (with the exception of birth control or anti-malarial drugs other than mefloquine/Lariam)."
          hasAsterisk
        />
      </div>

      {/* Box A - Lungs/Heart */}
      {getValue('q1_lungs_heart') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('A')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box A - Heart/Lungs Questions</span>
            {expandedBoxes.A ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.A && (
            <div className="p-4">
              <YesNoQuestion id="boxA_1" question="Chest surgery, heart surgery, heart valve surgery, an implantable medical device (stent, pacemaker, neurostimulator), pneumothorax, and/or chronic lung disease." hasAsterisk />
              <YesNoQuestion id="boxA_2" question="Asthma, wheezing, severe allergies, hay fever or congested airways within the last 12 months that limits my physical activity/exercise." hasAsterisk />
              <YesNoQuestion id="boxA_3" question="A problem or illness involving my heart such as: angina, chest pain on exertion, heart failure, immersion pulmonary edema, heart attack or stroke, OR am taking medication for any heart condition." hasAsterisk />
              <YesNoQuestion id="boxA_4" question="Recurrent bronchitis and currently coughing within the past 12 months, OR have been diagnosed with emphysema." hasAsterisk />
              <YesNoQuestion id="boxA_5" question="Symptoms affecting my lungs, breathing, heart and/or blood in the last 30 days that impair my physical or mental performance." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box B - Over 45 */}
      {getValue('q2_over_45') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('B')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box B - Over 45 Questions</span>
            {expandedBoxes.B ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.B && (
            <div className="p-4">
              <YesNoQuestion id="boxB_1" question="I currently smoke or inhale nicotine by other means." hasAsterisk />
              <YesNoQuestion id="boxB_2" question="I have a high cholesterol level." hasAsterisk />
              <YesNoQuestion id="boxB_3" question="I have high blood pressure." hasAsterisk />
              <YesNoQuestion id="boxB_4" question="I have had a close blood relative die suddenly or of cardiac disease or stroke before the age of 50, OR have a family history of heart disease before age 50." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box C - Eyes/Ears */}
      {getValue('q4_eyes_ears') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('C')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box C - Eyes/Ears Questions</span>
            {expandedBoxes.C ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.C && (
            <div className="p-4">
              <YesNoQuestion id="boxC_1" question="Sinus surgery within the last 6 months." hasAsterisk />
              <YesNoQuestion id="boxC_2" question="Ear disease or ear surgery, hearing loss, or problems with balance." hasAsterisk />
              <YesNoQuestion id="boxC_3" question="Recurrent sinusitis within the past 12 months." hasAsterisk />
              <YesNoQuestion id="boxC_4" question="Eye surgery within the past 3 months." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box D - Neurological */}
      {getValue('q6_neurological') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('D')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box D - Neurological Questions</span>
            {expandedBoxes.D ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.D && (
            <div className="p-4">
              <YesNoQuestion id="boxD_1" question="Head injury with loss of consciousness within the past 5 years." hasAsterisk />
              <YesNoQuestion id="boxD_2" question="Persistent neurologic injury or disease." hasAsterisk />
              <YesNoQuestion id="boxD_3" question="Recurring migraine headaches within the past 12 months, or take medications to prevent them." hasAsterisk />
              <YesNoQuestion id="boxD_4" question="Blackouts or fainting (full/partial loss of consciousness) within the last 5 years." hasAsterisk />
              <YesNoQuestion id="boxD_5" question="Epilepsy, seizures, or convulsions, OR take medications to prevent them." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box E - Psychological */}
      {getValue('q7_psychological') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('E')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box E - Psychological Questions</span>
            {expandedBoxes.E ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.E && (
            <div className="p-4">
              <YesNoQuestion id="boxE_1" question="Behavioral health, mental or psychological problems requiring medical/psychiatric treatment." hasAsterisk />
              <YesNoQuestion id="boxE_2" question="Major depression, suicidal ideation, panic attacks, uncontrolled bipolar disorder requiring medication/psychiatric treatment." hasAsterisk />
              <YesNoQuestion id="boxE_3" question="Been diagnosed with a mental health condition or a learning/developmental disorder that requires ongoing care or special accommodation." hasAsterisk />
              <YesNoQuestion id="boxE_4" question="An addiction to drugs or alcohol requiring treatment within the last 5 years." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box F - Back/Diabetes */}
      {getValue('q8_back_diabetes') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('F')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box F - Back/Diabetes Questions</span>
            {expandedBoxes.F ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.F && (
            <div className="p-4">
              <YesNoQuestion id="boxF_1" question="Recurrent back problems in the last 6 months that limit my everyday activity." hasAsterisk />
              <YesNoQuestion id="boxF_2" question="Back or spinal surgery within the last 12 months." hasAsterisk />
              <YesNoQuestion id="boxF_3" question="Diabetes, either drug or diet controlled, OR gestational diabetes within the last 12 months." hasAsterisk />
              <YesNoQuestion id="boxF_4" question="An uncorrected hernia that limits my physical abilities." hasAsterisk />
              <YesNoQuestion id="boxF_5" question="Active or untreated ulcers, problem wounds, or ulcer surgery within the last 6 months." hasAsterisk />
            </div>
          )}
        </div>
      )}

      {/* Box G - Stomach */}
      {getValue('q9_stomach') === true && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleBox('G')}
            className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100"
          >
            <span className="font-medium text-orange-800">Box G - Stomach/Intestine Questions</span>
            {expandedBoxes.G ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedBoxes.G && (
            <div className="p-4">
              <YesNoQuestion id="boxG_1" question="Ostomy surgery and do not have medical clearance to swim or engage in physical activity." hasAsterisk />
              <YesNoQuestion id="boxG_2" question="Dehydration requiring medical intervention within the last 7 days." hasAsterisk />
              <YesNoQuestion id="boxG_3" question="Active or untreated stomach or intestinal ulcers or ulcer surgery within the last 6 months." hasAsterisk />
              <YesNoQuestion id="boxG_4" question="Frequent heartburn, regurgitation, or gastroesophageal reflux disease (GERD)." hasAsterisk />
              <YesNoQuestion id="boxG_5" question="Active or uncontrolled ulcerative colitis or Crohn's disease." hasAsterisk />
              <YesNoQuestion id="boxG_6" question="Bariatric surgery within the last 12 months." hasAsterisk />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Digital Signature Component
const DigitalSignature = ({ label, value, onChange, name, date, onNameChange, onDateChange }) => {
  return (
    <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
      <h5 className="font-semibold text-[#0A2342]">{label}</h5>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Full Legal Name *</label>
          <Input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Type your full name"
            className="text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Date *</label>
          <Input
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            className="text-sm"
          />
        </div>
      </div>
      
      <label className="flex items-start gap-3 p-3 bg-white rounded border cursor-pointer hover:bg-gray-50">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 w-4 h-4 text-[#0A2342] rounded"
        />
        <span className="text-xs text-gray-700">
          By checking this box and typing my name above, I confirm this constitutes my legal electronic signature. 
          I have read, understood, and agree to all terms and conditions stated above.
        </span>
      </label>
    </div>
  );
};

// Main Booking Form Component
const TripBookingForm = ({ 
  trip, 
  user, 
  onSubmit, 
  onCancel 
}) => {
  const [step, setStep] = useState(1); // 1: Details, 2: Medical, 3: Waiver, 4: Review
  const [attendeeCount, setAttendeeCount] = useState(1);
  const [formData, setFormData] = useState({
    // Primary booker
    primaryName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    primaryEmail: user?.email || '',
    primaryPhone: user?.contact_number || '',
    primaryBirthdate: '',
    certificationLevel: '',
    emergencyContact: '',
    emergencyPhone: '',
    dietaryRequirements: '',
    // Medical questionnaire
    medical: {},
    medicalSignature: false,
    medicalSignatureName: '',
    medicalSignatureDate: new Date().toISOString().split('T')[0],
    // Liability waiver
    waiverAccepted: false,
    waiverSignatureName: '',
    waiverSignatureDate: new Date().toISOString().split('T')[0],
    // Additional attendees
    additionalAttendees: [],
    // Gear hire
    gearHire: {
      fullKit: false,
      hardGear: false,
      individualItems: {
        wetsuit: false,
        bcd: false,
        regulator: false,
        fins: false,
        mask: false,
        weightBelt: false
      },
      sizes: {
        bcd: '',
        wetsuit: '',
        fins: ''
      }
    }
  });

  const [showWaiverText, setShowWaiverText] = useState(false);

  // Gear hire pricing
  const GEAR_PRICES = {
    fullKit: 650, // BCD, Regulator, Wetsuit, Fins, Mask
    hardGear: 450, // BCD, Regulator, Weights
    individual: 200 // Per item: Wetsuit, BCD, Regulator, Fins
  };

  // Size options
  const SIZE_OPTIONS = {
    bcd: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    wetsuit: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    fins: ['XS (36-38)', 'S (38-40)', 'M (40-42)', 'L (42-44)', 'XL (44-46)', 'XXL (46-48)']
  };

  const availableSpots = trip.max_participants 
    ? trip.max_participants - (trip.current_participants || 0)
    : 20;

  const updateAttendeeCount = (newCount) => {
    const clampedCount = Math.max(1, Math.min(newCount, availableSpots));
    setAttendeeCount(clampedCount);
    
    const additionalCount = clampedCount - 1;
    const currentAdditional = formData.additionalAttendees;
    
    if (additionalCount > currentAdditional.length) {
      const newAttendees = [...currentAdditional];
      for (let i = currentAdditional.length; i < additionalCount; i++) {
        newAttendees.push({ 
          name: '', 
          email: '', 
          phone: '', 
          birthdate: '',
          certificationLevel: '',
          medical: {},
          medicalSignature: false,
          medicalSignatureName: '',
          medicalSignatureDate: new Date().toISOString().split('T')[0]
        });
      }
      setFormData(prev => ({ ...prev, additionalAttendees: newAttendees }));
    } else if (additionalCount < currentAdditional.length) {
      setFormData(prev => ({
        ...prev,
        additionalAttendees: currentAdditional.slice(0, additionalCount)
      }));
    }
  };

  const updateAdditionalAttendee = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.additionalAttendees];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, additionalAttendees: updated };
    });
  };

  const calculateGearCost = () => {
    const { gearHire } = formData;
    const tripDays = calculateTripDays();
    let gearCost = 0;

    if (gearHire.fullKit) {
      gearCost += GEAR_PRICES.fullKit * tripDays * attendeeCount;
    } else if (gearHire.hardGear) {
      gearCost += GEAR_PRICES.hardGear * tripDays * attendeeCount;
    } else {
      // Individual items
      const { individualItems } = gearHire;
      const itemCount = [individualItems.wetsuit, individualItems.bcd, individualItems.regulator, individualItems.fins].filter(Boolean).length;
      gearCost += GEAR_PRICES.individual * itemCount * tripDays * attendeeCount;
    }

    return gearCost;
  };

  const calculateTripDays = () => {
    if (!trip.start_date || !trip.end_date) return 1;
    const start = new Date(trip.start_date);
    const end = new Date(trip.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end days
    return diffDays || 1;
  };

  // Catering upgrade — only offered when the admin ticked `catered` AND set
  // a `catered_price > 0` on the trip. Priced per person per trip (flat, not
  // per day, matching how the admin enters it on the trip form).
  const cateringOffered = !!(trip.catered && Number(trip.catered_price) > 0);
  const [cateringOpted, setCateringOpted] = useState(false);
  const calculateCateringCost = () => (cateringOffered && cateringOpted ? Number(trip.catered_price) * attendeeCount : 0);

  const calculateTotal = () => {
    const tripCost = trip.price * attendeeCount;
    const gearCost = calculateGearCost();
    return tripCost + gearCost + calculateCateringCost();
  };

  const getSelectedGearSummary = () => {
    const { gearHire } = formData;
    const items = [];
    
    if (gearHire.fullKit) {
      items.push({ name: 'Full Kit (BCD, Regulator, Wetsuit, Fins, Mask)', price: GEAR_PRICES.fullKit });
    } else if (gearHire.hardGear) {
      items.push({ name: 'Hard Gear (BCD, Regulator, Weights)', price: GEAR_PRICES.hardGear });
    } else {
      if (gearHire.individualItems.wetsuit) items.push({ name: 'Wetsuit', price: GEAR_PRICES.individual });
      if (gearHire.individualItems.bcd) items.push({ name: 'BCD', price: GEAR_PRICES.individual });
      if (gearHire.individualItems.regulator) items.push({ name: 'Regulator', price: GEAR_PRICES.individual });
      if (gearHire.individualItems.fins) items.push({ name: 'Fins', price: GEAR_PRICES.individual });
      if (gearHire.individualItems.mask) items.push({ name: 'Mask', price: GEAR_PRICES.individual });
      if (gearHire.individualItems.weightBelt) items.push({ name: 'Weight Belt', price: GEAR_PRICES.individual });
    }
    
    return items;
  };

  const needsSizes = () => {
    const { gearHire } = formData;
    return gearHire.fullKit || gearHire.hardGear || 
           gearHire.individualItems.wetsuit || gearHire.individualItems.bcd || gearHire.individualItems.fins;
  };

  const updateGearHire = (field, value) => {
    setFormData(prev => {
      const newGearHire = { ...prev.gearHire };
      
      if (field === 'fullKit' && value) {
        // Clear other options when Full Kit is selected
        newGearHire.fullKit = true;
        newGearHire.hardGear = false;
        newGearHire.individualItems = { wetsuit: false, bcd: false, regulator: false, fins: false };
      } else if (field === 'hardGear' && value) {
        // Clear other options when Hard Gear is selected
        newGearHire.fullKit = false;
        newGearHire.hardGear = true;
        newGearHire.individualItems = { wetsuit: false, bcd: false, regulator: false, fins: false };
      } else if (field.startsWith('individual_')) {
        const itemName = field.replace('individual_', '');
        newGearHire.fullKit = false;
        newGearHire.hardGear = false;
        newGearHire.individualItems = { ...newGearHire.individualItems, [itemName]: value };
      } else if (field === 'fullKit' && !value) {
        newGearHire.fullKit = false;
      } else if (field === 'hardGear' && !value) {
        newGearHire.hardGear = false;
      } else if (field.startsWith('size_')) {
        const sizeName = field.replace('size_', '');
        newGearHire.sizes = { ...newGearHire.sizes, [sizeName]: value };
      }
      
      return { ...prev, gearHire: newGearHire };
    });
  };

  const validateStep1 = () => {
    if (!formData.primaryName || !formData.primaryEmail || !formData.primaryPhone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (!formData.primaryBirthdate) {
      toast.error('Please enter your date of birth');
      return false;
    }
    if (!formData.emergencyContact || !formData.emergencyPhone) {
      toast.error('Emergency contact is required');
      return false;
    }
    
    // Validate gear sizes if gear is selected
    const { gearHire } = formData;
    if (gearHire.fullKit) {
      if (!gearHire.sizes.bcd || !gearHire.sizes.wetsuit || !gearHire.sizes.fins) {
        toast.error('Please select sizes for all gear (BCD, Wetsuit, Fins)');
        return false;
      }
    } else if (gearHire.hardGear) {
      if (!gearHire.sizes.bcd) {
        toast.error('Please select BCD size');
        return false;
      }
    } else {
      if (gearHire.individualItems.bcd && !gearHire.sizes.bcd) {
        toast.error('Please select BCD size');
        return false;
      }
      if (gearHire.individualItems.wetsuit && !gearHire.sizes.wetsuit) {
        toast.error('Please select Wetsuit size');
        return false;
      }
      if (gearHire.individualItems.fins && !gearHire.sizes.fins) {
        toast.error('Please select Fins size');
        return false;
      }
    }
    
    for (let i = 0; i < formData.additionalAttendees.length; i++) {
      const att = formData.additionalAttendees[i];
      if (!att.name || !att.email || !att.birthdate) {
        toast.error(`Please fill in all details for attendee ${i + 2}`);
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    // Check primary medical form is complete (at least main questions answered)
    const mainQuestions = ['q1_lungs_heart', 'q2_over_45', 'q3_exercise', 'q4_eyes_ears', 'q5_surgery', 
                          'q6_neurological', 'q7_psychological', 'q8_back_diabetes', 'q9_stomach', 'q10_medications'];
    
    for (const q of mainQuestions) {
      if (formData.medical[q] === undefined || formData.medical[q] === null) {
        toast.error('Please answer all medical screening questions');
        return false;
      }
    }
    
    if (!formData.medicalSignature || !formData.medicalSignatureName) {
      toast.error('Please sign the medical questionnaire');
      return false;
    }

    // Check additional attendees
    for (let i = 0; i < formData.additionalAttendees.length; i++) {
      const att = formData.additionalAttendees[i];
      for (const q of mainQuestions) {
        if (att.medical?.[q] === undefined || att.medical?.[q] === null) {
          toast.error(`Please complete the medical questionnaire for attendee ${i + 2}`);
          return false;
        }
      }
      if (!att.medicalSignature || !att.medicalSignatureName) {
        toast.error(`Please sign the medical questionnaire for attendee ${i + 2}`);
        return false;
      }
    }

    return true;
  };

  const validateStep3 = () => {
    if (!formData.waiverAccepted || !formData.waiverSignatureName) {
      toast.error('Please read and sign the liability waiver');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
  };

  const handleSubmit = () => {
    if (!validateStep1() || !validateStep2() || !validateStep3()) return;
    
    const bookingData = {
      trip,
      attendeeCount,
      totalPrice: calculateTotal(),
      tripPrice: trip.price * attendeeCount,
      gearHireCost: calculateGearCost(),
      gearHire: formData.gearHire,
      gearSummary: getSelectedGearSummary(),
      cateringOpted: cateringOffered && cateringOpted,
      cateringCost: calculateCateringCost(),
      cateringPricePerPerson: cateringOffered ? Number(trip.catered_price) : 0,
      tripDays: calculateTripDays(),
      formData,
      // Explicit — bubbled up separately from the trip object so the parent
      // doesn't have to spelunk into trip.selected_destination.
      selectedDestination: trip.selected_destination || null,
      submittedAt: new Date().toISOString()
    };
    
    onSubmit(bookingData);
  };

  const stepTitles = ['Attendee Details', 'Medical Questionnaire', 'Liability Waiver', 'Review & Confirm'];

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-6">
        {stepTitles.map((title, idx) => (
          <div key={idx} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step > idx + 1 ? 'bg-green-500 text-white' :
              step === idx + 1 ? 'bg-[#FF6B35] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > idx + 1 ? '✓' : idx + 1}
            </div>
            <span className={`ml-2 text-xs hidden sm:inline ${step === idx + 1 ? 'text-[#0A2342] font-semibold' : 'text-gray-500'}`}>
              {title}
            </span>
            {idx < 3 && <div className="w-8 sm:w-16 h-0.5 mx-2 bg-gray-200" />}
          </div>
        ))}
      </div>

      {/* Trip Summary */}
      <div className="bg-[#F0EFEA] rounded-lg p-4 flex gap-4">
        <img src={trip.image_url} alt={trip.destination} className="w-20 h-14 object-cover rounded" />
        <div className="flex-1">
          <h4 className="font-semibold text-[#0A2342]">{trip.destination}</h4>
          {trip.selected_destination && (
            <p className="text-xs text-[#FF6B35] font-semibold mt-0.5" data-testid="booking-destination-line">
              Destination: <span className="text-[#0A2342] font-bold">{trip.selected_destination}</span>
            </p>
          )}
          <p className="text-sm text-gray-600">{trip.display_date || `${trip.start_date} - ${trip.end_date}`}</p>
          {trip.selected_accommodation && (
            <p className="text-xs text-[#0A2342] mt-0.5">Accommodation: <strong>{trip.selected_accommodation.name}</strong></p>
          )}
          {(() => {
            const basis = trip.selected_accommodation?.pricing_basis || 'pps';
            const label = { pps: 'per person sharing', pp: 'per person', single: 'single (solo room)' }[basis] || 'per person sharing';
            return (
              <p className="text-sm font-semibold text-[#FF6B35]">R{trip.price.toLocaleString()} {label}</p>
            );
          })()}
        </div>
      </div>

      {/* Step 1: Attendee Details */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Number of Attendees */}
          <div className="bg-white border rounded-lg p-4">
            <label className="block text-sm font-medium text-[#0A2342] mb-3">Number of Attendees</label>
            <div className="flex items-center gap-4">
              <Button type="button" variant="outline" size="icon" onClick={() => updateAttendeeCount(attendeeCount - 1)} disabled={attendeeCount <= 1}>
                <Minus className="w-4 h-4" />
              </Button>
              <span className="text-2xl font-bold text-[#0A2342] w-12 text-center">{attendeeCount}</span>
              <Button type="button" variant="outline" size="icon" onClick={() => updateAttendeeCount(attendeeCount + 1)} disabled={attendeeCount >= availableSpots}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Catering upgrade — only surfaces when the trip has `catered`
              ticked AND a catering price set. The toggle multiplies the
              catered_price by attendee count and rolls the amount into the
              order total (surfaces on the Review step + order confirmation). */}
          {cateringOffered && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="catering-upgrade-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#FF6B35]"
                  checked={cateringOpted}
                  onChange={(e) => setCateringOpted(e.target.checked)}
                  data-testid="catering-upgrade-toggle"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-[#0A2342]">🍴 Add Catering Upgrade</span>
                    <span className="text-sm font-bold text-[#FF6B35]" data-testid="catering-upgrade-price-per-person">
                      +R{Number(trip.catered_price).toLocaleString()} per person
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Meals prepared for you throughout the trip. Skip this if you&apos;re self-catering.</p>
                  {cateringOpted && (
                    <p className="text-sm font-semibold text-[#0A2342] mt-2" data-testid="catering-upgrade-total">
                      Catering total: R{calculateCateringCost().toLocaleString()} <span className="text-xs font-normal text-gray-500">({attendeeCount} {attendeeCount === 1 ? 'person' : 'people'})</span>
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          {/* Primary Booker */}
          <div className="space-y-4">
            <h4 className="font-semibold text-[#0A2342] border-b pb-2">Primary Booker (Attendee 1) <span className="text-red-500">*</span></h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Full Name *</label>
                <Input value={formData.primaryName} onChange={(e) => setFormData(prev => ({ ...prev, primaryName: e.target.value }))} placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date of Birth *</label>
                <Input type="date" value={formData.primaryBirthdate} onChange={(e) => setFormData(prev => ({ ...prev, primaryBirthdate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email *</label>
                <Input type="email" value={formData.primaryEmail} onChange={(e) => setFormData(prev => ({ ...prev, primaryEmail: e.target.value }))} placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone *</label>
                <Input value={formData.primaryPhone} onChange={(e) => setFormData(prev => ({ ...prev, primaryPhone: e.target.value }))} placeholder="+27 82 123 4567" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Certification Level</label>
                <Input value={formData.certificationLevel} onChange={(e) => setFormData(prev => ({ ...prev, certificationLevel: e.target.value }))} placeholder="e.g. Open Water, Advanced" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Dietary Requirements</label>
                <Input value={formData.dietaryRequirements} onChange={(e) => setFormData(prev => ({ ...prev, dietaryRequirements: e.target.value }))} placeholder="e.g. Vegetarian, Halal, None" />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <h4 className="font-semibold text-[#0A2342] border-b pb-2">Emergency Contact <span className="text-red-500">*</span></h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contact Name *</label>
                <Input value={formData.emergencyContact} onChange={(e) => setFormData(prev => ({ ...prev, emergencyContact: e.target.value }))} placeholder="Jane Smith" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contact Phone *</label>
                <Input value={formData.emergencyPhone} onChange={(e) => setFormData(prev => ({ ...prev, emergencyPhone: e.target.value }))} placeholder="+27 82 987 6543" />
              </div>
            </div>
          </div>

          {/* Gear Hire Section */}
          <div className="space-y-4">
            <h4 className="font-semibold text-[#0A2342] border-b pb-2">Gear Hire (Optional)</h4>
            <p className="text-sm text-gray-600">Need to hire diving gear? Select from the options below. Prices are per day for {calculateTripDays()} day(s).</p>
            
            {/* Full Kit */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="fullKit"
                  checked={formData.gearHire.fullKit}
                  onChange={(e) => updateGearHire('fullKit', e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded border-gray-300 focus:ring-[#FF6B35]"
                />
                <div className="flex-1">
                  <label htmlFor="fullKit" className="font-semibold text-[#0A2342] cursor-pointer">Full Kit</label>
                  <p className="text-sm text-gray-600">BCD, Regulator, Wetsuit, Fins, Mask</p>
                  <p className="text-[#FF6B35] font-bold mt-1">R{GEAR_PRICES.fullKit} per day</p>
                </div>
              </div>
            </div>

            {/* Hard Gear */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="hardGear"
                  checked={formData.gearHire.hardGear}
                  onChange={(e) => updateGearHire('hardGear', e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded border-gray-300 focus:ring-[#FF6B35]"
                />
                <div className="flex-1">
                  <label htmlFor="hardGear" className="font-semibold text-[#0A2342] cursor-pointer">Hard Gear</label>
                  <p className="text-sm text-gray-600">BCD, Regulator, Weights</p>
                  <p className="text-[#FF6B35] font-bold mt-1">R{GEAR_PRICES.hardGear} per day</p>
                </div>
              </div>
            </div>

            {/* Individual Items */}
            <div className="bg-white border rounded-lg p-4">
              <h5 className="font-semibold text-[#0A2342] mb-3">Individual Items</h5>
              <p className="text-sm text-gray-600 mb-3">R{GEAR_PRICES.individual} per item per day</p>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  { id: 'wetsuit', label: 'Wetsuit' },
                  { id: 'bcd', label: 'BCD' },
                  { id: 'regulator', label: 'Regulator' },
                  { id: 'fins', label: 'Fins' },
                  { id: 'mask', label: 'Mask' },
                  { id: 'weightBelt', label: 'Weight Belt' }
                ].map(item => (
                  <label key={item.id} className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.gearHire.individualItems[item.id]}
                      onChange={(e) => updateGearHire(`individual_${item.id}`, e.target.checked)}
                      className="w-4 h-4 text-[#FF6B35] rounded border-gray-300 focus:ring-[#FF6B35]"
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Size Selection - Show when gear requiring sizes is selected */}
            {needsSizes() && (
              <div className="bg-[#F0EFEA] border border-[#0A2342]/20 rounded-lg p-4">
                <h5 className="font-semibold text-[#0A2342] mb-3">Size Selection</h5>
                <p className="text-sm text-gray-600 mb-4">Please select sizes for the gear you're hiring:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* BCD Size */}
                  {(formData.gearHire.fullKit || formData.gearHire.hardGear || formData.gearHire.individualItems.bcd) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">BCD Size *</label>
                      <select
                        value={formData.gearHire.sizes.bcd}
                        onChange={(e) => updateGearHire('size_bcd', e.target.value)}
                        className="w-full border rounded-md p-2 text-sm focus:ring-[#FF6B35] focus:border-[#FF6B35]"
                        required
                      >
                        <option value="">Select size</option>
                        {SIZE_OPTIONS.bcd.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Wetsuit Size */}
                  {(formData.gearHire.fullKit || formData.gearHire.individualItems.wetsuit) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Wetsuit Size *</label>
                      <select
                        value={formData.gearHire.sizes.wetsuit}
                        onChange={(e) => updateGearHire('size_wetsuit', e.target.value)}
                        className="w-full border rounded-md p-2 text-sm focus:ring-[#FF6B35] focus:border-[#FF6B35]"
                        required
                      >
                        <option value="">Select size</option>
                        {SIZE_OPTIONS.wetsuit.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Fins Size */}
                  {(formData.gearHire.fullKit || formData.gearHire.individualItems.fins) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fins Size *</label>
                      <select
                        value={formData.gearHire.sizes.fins}
                        onChange={(e) => updateGearHire('size_fins', e.target.value)}
                        className="w-full border rounded-md p-2 text-sm focus:ring-[#FF6B35] focus:border-[#FF6B35]"
                        required
                      >
                        <option value="">Select size</option>
                        {SIZE_OPTIONS.fins.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gear Cost Summary */}
            {calculateGearCost() > 0 && (
              <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-[#0A2342]">Gear Hire Total</p>
                    <p className="text-sm text-gray-600">{getSelectedGearSummary().map(g => g.name).join(', ')} × {calculateTripDays()} days × {attendeeCount} {attendeeCount > 1 ? 'people' : 'person'}</p>
                  </div>
                  <p className="text-xl font-bold text-[#FF6B35]">R{calculateGearCost().toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>

          {/* Additional Attendees */}
          {attendeeCount > 1 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-[#0A2342] border-b pb-2">Additional Attendees</h4>
              {formData.additionalAttendees.map((att, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Attendee {idx + 2}</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input value={att.name} onChange={(e) => updateAdditionalAttendee(idx, 'name', e.target.value)} placeholder="Full Name *" />
                    <Input type="date" value={att.birthdate} onChange={(e) => updateAdditionalAttendee(idx, 'birthdate', e.target.value)} placeholder="Date of Birth *" />
                    <Input type="email" value={att.email} onChange={(e) => updateAdditionalAttendee(idx, 'email', e.target.value)} placeholder="Email *" />
                    <Input value={att.phone} onChange={(e) => updateAdditionalAttendee(idx, 'phone', e.target.value)} placeholder="Phone" />
                    <Input value={att.certificationLevel} onChange={(e) => updateAdditionalAttendee(idx, 'certificationLevel', e.target.value)} placeholder="Certification Level" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Medical Questionnaire */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Medical Questionnaire</p>
                <p className="text-xs text-blue-700 mt-1">
                  Each participant must complete and sign the medical questionnaire. This is required for your safety.
                  You can also download the <a href="https://customer-assets.emergentagent.com/job_7d07c68e-45eb-435c-844c-a4650369bb63/artifacts/0vxx3q95_Diving_Medical_Guidance_EN_English_2023-05-31.pdf" target="_blank" rel="noopener noreferrer" className="underline">Medical Guidance PDF</a> for reference.
                </p>
              </div>
            </div>
          </div>

          {/* Primary Booker Medical */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-[#0A2342] text-white p-3">
              <h5 className="font-semibold">Medical Questionnaire - {formData.primaryName || 'Primary Booker'}</h5>
            </div>
            <div className="p-4">
              <MedicalQuestionnaire formData={formData} setFormData={setFormData} />
              
              <div className="mt-6 pt-4 border-t">
                <p className="text-xs text-gray-600 mb-3">
                  I have answered all questions honestly, and understand that I accept responsibility for any consequences 
                  resulting from any questions I may have answered inaccurately or for my failure to disclose any existing or past health conditions.
                </p>
                <DigitalSignature
                  label="Medical Questionnaire Signature"
                  value={formData.medicalSignature}
                  onChange={(v) => setFormData(prev => ({ ...prev, medicalSignature: v }))}
                  name={formData.medicalSignatureName}
                  onNameChange={(v) => setFormData(prev => ({ ...prev, medicalSignatureName: v }))}
                  date={formData.medicalSignatureDate}
                  onDateChange={(v) => setFormData(prev => ({ ...prev, medicalSignatureDate: v }))}
                />
              </div>
            </div>
          </div>

          {/* Additional Attendees Medical */}
          {formData.additionalAttendees.map((att, idx) => (
            <div key={idx} className="border rounded-lg overflow-hidden">
              <div className="bg-[#0A2342] text-white p-3">
                <h5 className="font-semibold">Medical Questionnaire - {att.name || `Attendee ${idx + 2}`}</h5>
              </div>
              <div className="p-4">
                <MedicalQuestionnaire formData={formData} setFormData={setFormData} attendeeIndex={idx} />
                
                <div className="mt-6 pt-4 border-t">
                  <DigitalSignature
                    label={`Signature - ${att.name || `Attendee ${idx + 2}`}`}
                    value={att.medicalSignature}
                    onChange={(v) => updateAdditionalAttendee(idx, 'medicalSignature', v)}
                    name={att.medicalSignatureName}
                    onNameChange={(v) => updateAdditionalAttendee(idx, 'medicalSignatureName', v)}
                    date={att.medicalSignatureDate}
                    onDateChange={(v) => updateAdditionalAttendee(idx, 'medicalSignatureDate', v)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 3: Liability Waiver */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <p className="text-sm text-orange-800 font-medium">Release and Liability Waiver</p>
                <p className="text-xs text-orange-700 mt-1">
                  Please read the following waiver carefully. By signing, you acknowledge the inherent risks of diving activities.
                </p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowWaiverText(!showWaiverText)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
            >
              <span className="font-medium text-[#0A2342]">Release and Waiver of Liability Agreement</span>
              {showWaiverText ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showWaiverText && (
              <div className="p-4 bg-white max-h-96 overflow-y-auto text-xs text-gray-700 space-y-3 border-t">
                <h5 className="font-bold">Declaration of Safe Diving Practices and Statement of Understanding</h5>
                <p className="italic">Subject to local laws and regulations</p>
                
                <p>As an ISC student and/or as a certified diver, I understand and I am aware of the principle that I should:</p>
                
                <ul className="list-disc pl-4 space-y-1">
                  <li>Engage only in diving activities consistent with my training, certification, experience, and conditions consistent with my training and avoid diving beyond my limits without proper training.</li>
                  <li>Maintain good physical fitness and mental health for diving.</li>
                  <li>Avoid being under the influence of dangerous drugs or alcohol when diving.</li>
                  <li>Keep proficient in diving skills, reviewing them in controlled conditions after 6 (six) months of diving inactivity.</li>
                  <li>When diving new sites, obtain a formal diving orientation from a knowledgeable, local source.</li>
                  <li>I shall refrain from diving if I feel uncomfortable if there is a problem discovered following the equipment assembly and checks.</li>
                  <li>Maintain equipment and follow manufacturer's guidelines for service, maintenance, and replacement consumables.</li>
                  <li>Deny use of my SCUBA equipment to uncertified divers.</li>
                  <li>Respect and follow the advice of those supervising my diving activities.</li>
                  <li>Plan dives within reasonable safe limits.</li>
                  <li>Avoid decompression diving unless trained and certified to do so.</li>
                  <li>Monitoring no-decompression limit, dive time/s and gas pressures/volume and close dive buddy contact.</li>
                  <li>Never breath-hold or skip breaths when breathing compressed gas.</li>
                  <li>Use surface support.</li>
                </ul>

                <h6 className="font-bold mt-4">Release and Waiver of Liability Agreement</h6>
                
                <p>Scuba diving, Freediving and Snorkeling has inherent risks that the participant assumes. ISC training professionals conduct these activities under strict standards.</p>
                
                <p>I hereby release, remiss, and discharge JustDive, Scuba Crew; DIVECC PTY LTD T/as International Scuba Certification (ISC), their respective affiliates, shareholders, employees, facilities, instructors, and affiliated personnel from all claims, demands, actions, causes of action of any sort, arising out of law or in equity for injury sustained to myself or my property during my presence and/or participation in activities.</p>
                
                <p>I acknowledge that freediving; snorkeling; and/or diving with compressed air, oxygen-enriched gas, involves certain inherent risks that may cause injury or even death including but not limited to: drowning, pressure related injuries, equipment failure, decompression sickness, narcosis, perils of the sea, or acts of a fellow diver.</p>
                
                <p>I acknowledge that practical training may be conducted at a location remote from a recompression chamber and/or professional medical services. I agree to proceed with the diving activities despite my being fully aware of the proximity of medical assistance. I assume all these risks.</p>
                
                <p>I intend by this release to waive all claims for negligence, products liability, or breach of warranty against the Dive Center, including claims for personal injury to myself or for damage to my property.</p>
                
                <p className="font-semibold">I HAVE READ AND UNDERSTOOD THE FOREGOING RELEASE, AND BY AFFIXING MY DIGITAL SIGNATURE TO IT, I SIGNIFY MY CLEAR INTENTION TO BE LEGALLY BOUND BY IT.</p>
                
                <p>IF THE PARTICIPANT IS A LEGAL MINOR, I, THE UNDERSIGNED PARENT OR GUARDIAN, HEREBY ACCEPT RESPONSIBILITY FOR THE MINOR.</p>
              </div>
            )}
          </div>

          <DigitalSignature
            label="Liability Waiver Signature - I accept the terms above"
            value={formData.waiverAccepted}
            onChange={(v) => setFormData(prev => ({ ...prev, waiverAccepted: v }))}
            name={formData.waiverSignatureName}
            onNameChange={(v) => setFormData(prev => ({ ...prev, waiverSignatureName: v }))}
            date={formData.waiverSignatureDate}
            onDateChange={(v) => setFormData(prev => ({ ...prev, waiverSignatureDate: v }))}
          />
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800 font-medium">Review Your Booking</p>
            <p className="text-xs text-green-700 mt-1">Please review your details before adding to cart.</p>
          </div>

          <div className="bg-white border rounded-lg divide-y">
            <div className="p-4">
              <h5 className="font-semibold text-[#0A2342] mb-2">Trip Details</h5>
              <p className="text-sm">{trip.destination}</p>
              {trip.selected_destination && (
                <p className="text-xs text-[#FF6B35] font-semibold mt-1" data-testid="review-destination-line">
                  Destination locked: <span className="text-[#0A2342]">{trip.selected_destination}</span>
                </p>
              )}
              <p className="text-sm text-gray-600">{trip.display_date || `${trip.start_date} - ${trip.end_date}`}</p>
            </div>
            
            <div className="p-4">
              <h5 className="font-semibold text-[#0A2342] mb-2">Attendees ({attendeeCount})</h5>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>1. {formData.primaryName}</span>
                  <Badge className="bg-green-100 text-green-800">Medical ✓</Badge>
                </div>
                {formData.additionalAttendees.map((att, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{idx + 2}. {att.name}</span>
                    <Badge className="bg-green-100 text-green-800">Medical ✓</Badge>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4">
              <h5 className="font-semibold text-[#0A2342] mb-2">Documents Signed</h5>
              <div className="space-y-1 text-sm">
                <p className="text-green-700">✓ Medical Questionnaire(s) completed and signed</p>
                <p className="text-green-700">✓ Liability Waiver accepted and signed</p>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-[#0A2342] text-white rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-300">
                <span>Trip ({attendeeCount} {attendeeCount === 1 ? 'person' : 'people'} × R{trip.price.toLocaleString()})</span>
                <span>R{(trip.price * attendeeCount).toLocaleString()}</span>
              </div>
              
              {calculateGearCost() > 0 && (
                <div className="flex justify-between text-sm text-gray-300">
                  <span>
                    Gear Hire ({getSelectedGearSummary().map(g => g.name).join(', ')})
                    <br />
                    <span className="text-xs">× {calculateTripDays()} days × {attendeeCount} {attendeeCount > 1 ? 'people' : 'person'}</span>
                  </span>
                  <span>R{calculateGearCost().toLocaleString()}</span>
                </div>
              )}
              
              {calculateGearCost() > 0 && formData.gearHire.sizes && (
                <div className="text-xs text-gray-400 pt-2 border-t border-gray-600">
                  <span className="font-medium">Sizes: </span>
                  {formData.gearHire.sizes.bcd && <span>BCD: {formData.gearHire.sizes.bcd}</span>}
                  {formData.gearHire.sizes.wetsuit && <span>{formData.gearHire.sizes.bcd ? ' | ' : ''}Wetsuit: {formData.gearHire.sizes.wetsuit}</span>}
                  {formData.gearHire.sizes.fins && <span>{(formData.gearHire.sizes.bcd || formData.gearHire.sizes.wetsuit) ? ' | ' : ''}Fins: {formData.gearHire.sizes.fins}</span>}
                </div>
              )}

              {cateringOffered && cateringOpted && (
                <div className="flex justify-between text-sm text-gray-300" data-testid="review-catering-line">
                  <span>
                    🍴 Catering upgrade ({attendeeCount} × R{Number(trip.catered_price).toLocaleString()})
                  </span>
                  <span>R{calculateCateringCost().toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-gray-600">
                <span className="text-lg font-semibold">Total</span>
                <span className="text-2xl font-bold">R{calculateTotal().toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
        >
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        
        {step < 4 ? (
          <Button onClick={handleNext} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
            Continue
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 px-8">
            Add to Cart
          </Button>
        )}
      </div>
    </div>
  );
};

export default TripBookingForm;
